// Impact financier des pannes : synthèse annuelle par équipement et par mois (coûts directs + pertes de production).
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Grid, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { BuildOutlined, PaymentsOutlined, PrintOutlined, TimerOutlined, TrendingDownOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { panneAPI } from '../../services/api';
import { apiError, fcfa } from '../../utils/format';
import { tokens } from '../../theme';
import { CATEGORIES, CategorieIcon } from './common';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const heures = (h) => (h >= 48 ? `${Math.round(h / 24)} j` : `${Math.round(h)} h`);
const M = ({ v, bold, color }) => <Box component="span" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', fontWeight: bold ? 700 : undefined, color }}>{v}</Box>;

const PannesCouts = () => {
  const navigate = useNavigate();
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { setD(null); panneAPI.synthese(annee).then((r) => setD(r.data)).catch((e) => setError(apiError(e))); }, [annee]);
  const t = d?.totaux;
  const max = d ? Math.max(1, ...d.par_mois.map((m) => m.total)) : 1;

  return (
    <Layout title="Pannes — impact financier" plain
      actions={(
        <>
          <TextField select size="small" label="Année" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} sx={{ minWidth: 110 }}>
            {(d?.annees || [annee]).map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Imprimer</Button>
        </>
      )}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {t && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><Kpi compact icon={PaymentsOutlined} tint="red" label="Impact total" value={fcfa(t.total)} valueSize={22} sub={`F en ${annee}`} /></Grid>
            <Grid item xs={6} md={3}><Kpi compact icon={BuildOutlined} tint="amber" label="Coûts de réparation" value={fcfa(t.cout_direct)} valueSize={22}
              sub={`pièces ${fcfa(t.pieces)} · MO ${fcfa(t.main_oeuvre)} · prest. ${fcfa(t.prestataire)} · autres ${fcfa(t.autres)}`} /></Grid>
            <Grid item xs={6} md={3}><Kpi compact icon={TrendingDownOutlined} tint="navy" label="Pertes de production" value={fcfa(t.perte)} valueSize={22} sub={`${t.poteaux_perdus} poteaux non produits`} /></Grid>
            <Grid item xs={6} md={3}><Kpi compact icon={TimerOutlined} tint="gray" label="Pannes" value={t.nb} valueSize={22} sub={`${t.arrets} arrêt(s) · ${heures(t.heures)} d'immobilisation`} /></Grid>
          </Grid>
          {t.sans_impact > 0 && <Alert severity="info" sx={{ mb: 2 }}>{t.sans_impact} panne(s) de {annee} sans impact financier renseigné : ouvrez-les pour le compléter.</Alert>}

          <Panel title="Par équipement" flush sx={{ mb: 2 }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Équipement</TableCell><TableCell align="right">Pannes</TableCell><TableCell align="right">Immobilisation</TableCell>
                    <TableCell align="right">Pièces</TableCell><TableCell align="right">Main-d'œuvre</TableCell><TableCell align="right">Prestataire</TableCell>
                    <TableCell align="right">Autres</TableCell><TableCell align="right">Perte production</TableCell><TableCell align="right">Total (F)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {d.par_equipement.map((e) => (
                    <TableRow key={e.equipement} hover sx={{ cursor: 'pointer' }} onClick={() => navigate('/pannes', { state: { q: e.equipement } })}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CategorieIcon categorie={e.categorie} sx={{ fontSize: 18, color: tokens.steel }} />
                          <Box><Typography sx={{ fontSize: 14, fontWeight: 600 }}>{e.equipement}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{CATEGORIES[e.categorie]?.label}{e.arrets ? ` · ${e.arrets} arrêt(s)` : ''}</Typography></Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right"><M v={e.nb} /></TableCell><TableCell align="right"><M v={heures(e.heures)} /></TableCell>
                      <TableCell align="right"><M v={fcfa(e.pieces)} /></TableCell><TableCell align="right"><M v={fcfa(e.main_oeuvre)} /></TableCell>
                      <TableCell align="right"><M v={fcfa(e.prestataire)} /></TableCell><TableCell align="right"><M v={fcfa(e.autres)} /></TableCell>
                      <TableCell align="right"><M v={fcfa(e.perte)} /></TableCell><TableCell align="right"><M v={fcfa(e.total)} bold color={e.total ? '#B4232A' : undefined} /></TableCell>
                    </TableRow>
                  ))}
                  {!d.par_equipement.length && <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucune panne en {annee}.</TableCell></TableRow>}
                  {d.par_equipement.length > 0 && (
                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: `2px solid ${tokens.line}` } }}>
                      <TableCell>Total</TableCell><TableCell align="right"><M v={t.nb} /></TableCell><TableCell align="right"><M v={heures(t.heures)} /></TableCell>
                      <TableCell align="right"><M v={fcfa(t.pieces)} /></TableCell><TableCell align="right"><M v={fcfa(t.main_oeuvre)} /></TableCell>
                      <TableCell align="right"><M v={fcfa(t.prestataire)} /></TableCell><TableCell align="right"><M v={fcfa(t.autres)} /></TableCell>
                      <TableCell align="right"><M v={fcfa(t.perte)} /></TableCell><TableCell align="right"><M v={fcfa(t.total)} /></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Panel>

          <Panel title="Par mois" flush>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>Mois</TableCell><TableCell align="right">Pannes</TableCell><TableCell align="right">Réparations</TableCell>
                    <TableCell align="right">Pertes</TableCell><TableCell align="right">Total (F)</TableCell><TableCell sx={{ width: '30%' }} /></TableRow>
                </TableHead>
                <TableBody>
                  {d.par_mois.map((m, i) => (
                    <TableRow key={m.mois}>
                      <TableCell>{MOIS[i]}</TableCell><TableCell align="right"><M v={m.nb || '—'} /></TableCell>
                      <TableCell align="right"><M v={m.cout_direct ? fcfa(m.cout_direct) : '—'} /></TableCell>
                      <TableCell align="right"><M v={m.perte ? fcfa(m.perte) : '—'} /></TableCell>
                      <TableCell align="right"><M v={m.total ? fcfa(m.total) : '—'} bold={!!m.total} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: tokens.lineSoft, width: `${(100 * m.total) / max}%`, minWidth: m.total ? 4 : 0 }}>
                          <Box sx={{ width: `${m.total ? (100 * m.cout_direct) / m.total : 0}%`, bgcolor: '#C9850F' }} />
                          <Box sx={{ flex: 1, bgcolor: '#3566A8' }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', gap: 2, p: 1.5, fontSize: 12.5, color: 'text.secondary' }}>
              <span><Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#C9850F', borderRadius: 0.5, mr: 0.5 }} />Réparations</span>
              <span><Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#3566A8', borderRadius: 0.5, mr: 0.5 }} />Pertes de production</span>
            </Box>
          </Panel>
        </>
      )}
      {!d && !error && 'Chargement…'}
    </Layout>
  );
};

export default PannesCouts;
